/** @jsxImportSource react */
import clsxm from '@common/lib/clsxm';
import ExpertHelpCta from '@react/cta/ExpertHelpCta';
import Field from '@react/form/components/Field';
import { eventNames, useEvents } from '@react/services/events';
import Carousel from '@react/shared/components/Carousel';
import {
  formatDateWithSlashes,
  getTourTileDates,
  monthNameToAbbreviations,
  months,
  tourTimeOptions,
} from '@sly/core/helpers/date';
import { leadSourceMap } from '@sly/core/src/constants/uuidActions';
import { Agent } from '@sly/core/src/types';
import dayjs from 'dayjs';
import { useState } from 'react';

export const DateTile = ({
  date,
  onClick,
  selectedDate,
  id,
}: {
  date: string;
  id: string;
  selectedDate: string;
  onClick: (date: string) => void;
}) => {
  const monthAbbr = monthNameToAbbreviations[months[dayjs(date).month()]];
  const day = dayjs(date).date();
  const dayAbbr = dayjs(date).format('ddd');

  return (
    <div
      id={id}
      onClick={() => onClick(formatDateWithSlashes(dayjs(date).toDate()))}
      className={clsxm(
        'font-b-s flex w-24 cursor-pointer flex-col items-center rounded-lg border border-slate-200 py-3 px-2.5 shadow-md',
        selectedDate === formatDateWithSlashes(dayjs(date).toDate()) &&
          '!border-viridian-base bg-viridian-lighter-90'
      )}
    >
      <div>{dayAbbr}</div>
      <div className='font-t-m-azo'>{day}</div>
      <div>{monthAbbr.toUpperCase()}</div>
    </div>
  );
};

const InlineTour = ({
  className,
  agent,
}: {
  className?: string;
  agent: Agent;
}) => {
  const dates = getTourTileDates();

  const [selectedDate, setSelectedDate] = useState<string>(
    formatDateWithSlashes(dayjs(dates[0]).toDate())
  );

  const [selectedTime, setSelectedTime] = useState<string>(
    tourTimeOptions[0].value
  );

  const { events } = useEvents();

  return (
    <div className={clsxm('flex flex-col gap-4', className)}>
      <Carousel
        className='mx-auto mb-6 w-auto max-w-[640px]'
        innerClassName='!auto-cols-[100px] !w-auto !max-w-[584px] !gap-4 !mx-auto'
        itemsQty={dates.length}
      >
        {dates.map((date) => {
          const fomattedDate = formatDateWithSlashes(dayjs(date).toDate());
          return (
            <DateTile
              key={fomattedDate}
              id={`tour-date-${fomattedDate}`}
              onClick={(date: string) => {
                events.track(eventNames.ButtonClicked, {
                  cta: 'Schedule tour (open dates)',
                  text: 'Request Tour',
                });
                setSelectedDate(date);
              }}
              date={date}
              selectedDate={selectedDate}
            />
          );
        })}
      </Carousel>

      <div>
        <Field
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setSelectedTime(e.target.value);
          }}
          name='tourTimeText'
          type='select'
          label='Select a time'
          className='!mb-6'
          options={tourTimeOptions}
          value={selectedTime}
          message={undefined}
          invalid={undefined}
          warning={undefined}
          success={undefined}
          placeholder={undefined}
          hideErrors={undefined}
          labelRight={undefined}
          wideWidth={undefined}
          hideValue={undefined}
          showCharacterCount={undefined}
          required={undefined}
          leftMargin={undefined}
          forceRow={undefined}
          classNameLabelWrapper={undefined}
          hideBorder={undefined}
        />
      </div>

      <ExpertHelpCta
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        className='w-full lg:w-auto'
        agent={agent}
        eventProps={{
          location: 'agent profile page - calendar',
          text: 'Schedule a free consultation',
        }}
        ctaProps={{
          text: 'Schedule a free consultation',
          leadSource: leadSourceMap.AskAgentAgentPageLS,
          variant: 'prmr',
          palette: 'primary',
        }}
        initialMessage={`I would love to schedule a free 15 minute consultation with you on ${selectedDate} during the ${
          selectedTime.charAt(0).toLowerCase() + selectedTime.slice(1)
        }`}
      />
    </div>
  );
};

export default InlineTour;
